import type { PostDoc, ProfileDoc, Widget } from '../contract.js';
import type { AppStore } from './store.js';
import type { AppState, Patch } from './types.js';

/** Changing the document being edited: your post draft or your profile draft (`docNext`, `docMut`, `patchW`). */

/** The mutable copy handed to an edit: both drafts have `items` and `lines`; the profile also has name, colour, picture and description. */
export type DraftDoc = PostDoc | ProfileDoc;

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/**
 * `docNext`: the state patch for one edit, or null when nothing can be edited (no draft, or saving).
 * Any edit also dismisses an open confirmation prompt.
 */
export function docNext(st: AppState, edit: (doc: DraftDoc) => void): Patch | null {
  if (st.view === 'compose' && st.draft) {
    if (st.cStatus === 'saving') return null;
    const draft = clone(st.draft);
    edit(draft);
    return { draft, cAsk: null };
  }
  if (st.view === 'profile' && st.pe) {
    if (st.peStatus === 'saving') return null;
    const pe = clone(st.pe);
    edit(pe);
    return { pe, peAsk: false };
  }
  return null;
}

/** `docMut`: apply one edit, with extra state changes. Returns whether it was applied. */
export function docMut(s: AppStore, edit: (doc: DraftDoc) => void, extra: Patch = {}): boolean {
  const patch = docNext(s.getState(), edit);
  if (patch) s.set({ ...patch, ...extra });
  return !!patch;
}

/** `patchW`: change fields of one widget. */
export function patchW(s: AppStore, id: string, patch: Partial<Widget>): void {
  docMut(s, (doc) => { for (const item of doc.items) if (item.id === id) Object.assign(item, patch); });
}

/** The Colour lines switch: flips between colour and plain; a missing value counts as colour. */
export function toggleLines(s: AppStore): void {
  docMut(s, (doc) => { doc.lines = doc.lines === 'plain' ? 'colour' : 'plain'; });
}
